import { Component, OnDestroy, NgZone, OnInit } from '@angular/core';
import { trigger, transition, useAnimation } from '@angular/animations';
import { fadeIn } from 'ng-animate';
import { Observable, of, iif } from 'rxjs';
import { SubscriptionManager } from '@zelis/platform-ui-components';
import { Breakpoints } from '@classes/breakpoints.class';
import { GuidedSearchService } from '@services/guided-search/guided-search.service';
import { TermAutosuggest } from '@classes/term-autosuggest.class';
import { Procedure } from '@interfaces/procedure.model';
import { ProceduresService } from '@services/procedures.service';
import { SettingsService } from '@services/settings.service';
import { first, switchMap, filter, map } from 'rxjs/operators';
import { TrackByUtilities } from '@utilities/trackByFn.utilities';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-promotional-procedures',
  templateUrl: './promotional-procedures.component.html',
  styleUrls: ['./promotional-procedures.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(
        '* => *',
        useAnimation(fadeIn, {
          params: { timing: 1.1 },
        })
      ),
    ]),
  ],
})
export class PromotionalProceduresComponent implements OnInit, OnDestroy {
  public procedures: Procedure[] = [];
  public subscriptions = new SubscriptionManager();
  public trackByFnItemId: any = TrackByUtilities.trackByFnItemId;
  private promotionalProcedures = [];

  constructor(
    public breakpoints: Breakpoints,
    private guidedSearchService: GuidedSearchService,
    private proceduresService: ProceduresService,
    private termAutosuggest: TermAutosuggest,
    private settingsService: SettingsService,
    private ngZone: NgZone,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.subscribeToTranslationChange();
    this.setPromotedProcedures();
  }

  ngOnDestroy() {
    this.subscriptions.destroy();
  }

  public procedureSearch(procedureId: number): void {
    this.ngZone.run(() => {
      this.termAutosuggest.activateSearch({
        type: 'procedures',
        id: procedureId,
      });
    });
  }

  public openGuidedSearchProcedures(): void {
    this.guidedSearchService.openGuidedSearchByType('procedures');
  }

  private setPromotedProcedures(): void {
    this.getPromotedProceduresSetting()
      .pipe(
        map(
          (promotionalProcedures: any) =>
            (promotionalProcedures && promotionalProcedures.procedure_ids) || []
        ),
        switchMap((promotionalProcedures: number[]) => {
          this.promotionalProcedures = promotionalProcedures;
          return this.filterPromotionalProcedures(promotionalProcedures);
        })
      )
      .subscribe(
        (procedures) => (this.procedures = this.sortProcedures(procedures))
      );
  }

  private filterPromotionalProcedures(
    promotionalProcedures: number[]
  ): Observable<any> {
    return iif(
      () => promotionalProcedures.length > 0,
      this.getFilteredPromotionalProcedures(promotionalProcedures),
      of([])
    );
  }

  private getFilteredPromotionalProcedures(promotionalProcedures: number[]) {
    return this.proceduresService.proceduresUpdated.pipe(
      filter((procedures) => procedures && procedures.length > 0),
      map((procedures: Procedure[]) => {
        return procedures.filter((procedure) =>
          promotionalProcedures.includes(procedure.id)
        );
      })
    );
  }

  private sortProcedures(procedures: Procedure[]): Procedure[] {
    return procedures.sort((previous, current) => {
      return (
        this.promotionalProcedures.indexOf(previous.id) -
        this.promotionalProcedures.indexOf(current.id)
      );
    });
  }

  private getPromotedProceduresSetting(): Observable<string> {
    return this.settingsService
      .getSetting('promotional_procedures')
      .pipe(first());
  }

  private subscribeToTranslationChange(): void {
    this.subscriptions.add(
      this.translateService.onLangChange.subscribe((event) => {
        this.setPromotedProcedures();
      })
    );
  }
}
